import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare,
  MessageCircle,
  Phone,
  PhoneCall,
  PhoneMissed,
  Reply,
  CheckCheck,
  FileText,
  Calendar,
  CalendarCheck,
  CalendarX,
  RefreshCw,
  UserPlus,
  Award,
  Search,
  X,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Clock,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHeader = () => ({ Authorization: 'Bearer ' + localStorage.getItem('session_token') });

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
};

// ── Event Type Config ─────────────────────────────────────────────────────────
const EVENT_CONFIG = {
  sms_sent:          { icon: MessageSquare, color: 'bg-blue-100 text-blue-600',     label: 'SMS Sent',           filter: 'sms' },
  sms_delivered:     { icon: MessageSquare, color: 'bg-green-100 text-green-600',   label: 'SMS Delivered',      filter: 'sms' },
  sms_failed:        { icon: MessageSquare, color: 'bg-red-100 text-red-500',       label: 'SMS Failed',         filter: 'sms' },
  sms_reply:         { icon: Reply,         color: 'bg-purple-100 text-purple-600', label: 'SMS Reply',          filter: 'sms' },
  whatsapp_sent:     { icon: MessageCircle, color: 'bg-blue-100 text-blue-600',     label: 'WhatsApp Sent',      filter: 'whatsapp' },
  whatsapp_delivered:{ icon: MessageCircle, color: 'bg-yellow-100 text-yellow-600', label: 'WhatsApp Delivered', filter: 'whatsapp' },
  whatsapp_read:     { icon: CheckCheck,    color: 'bg-green-100 text-green-600',   label: 'WhatsApp Read',      filter: 'whatsapp' },
  whatsapp_reply:    { icon: Reply,         color: 'bg-emerald-100 text-emerald-600',label: 'WhatsApp Reply',    filter: 'whatsapp' },
  call_initiated:    { icon: Phone,         color: 'bg-blue-100 text-blue-600',     label: 'Call Initiated',     filter: 'voice' },
  call_answered:     { icon: PhoneCall,     color: 'bg-green-100 text-green-600',   label: 'Call Answered',      filter: 'voice' },
  call_not_answered: { icon: PhoneMissed,   color: 'bg-red-100 text-red-500',       label: 'Call Not Answered',  filter: 'voice' },
  call_completed:    { icon: PhoneCall,     color: 'bg-emerald-100 text-emerald-600',label: 'Call Completed',    filter: 'voice' },
  note_added:        { icon: FileText,      color: 'bg-amber-100 text-amber-600',   label: 'Note Added',         filter: 'notes' },
  demo_scheduled:    { icon: Calendar,      color: 'bg-purple-100 text-purple-600', label: 'Demo Scheduled',     filter: 'demo' },
  demo_completed:    { icon: CalendarCheck, color: 'bg-green-100 text-green-600',   label: 'Demo Completed',     filter: 'demo' },
  demo_cancelled:    { icon: CalendarX,     color: 'bg-red-100 text-red-500',       label: 'Demo Cancelled',     filter: 'demo' },
  status_changed:    { icon: RefreshCw,     color: 'bg-gray-100 text-gray-600',     label: 'Status Changed',     filter: 'all' },
  contact_created:   { icon: UserPlus,      color: 'bg-blue-100 text-blue-600',     label: 'Contact Created',    filter: 'all' },
  partner_converted: { icon: Award,         color: 'bg-yellow-100 text-yellow-600', label: 'Partner Converted',  filter: 'all' },
};

const FILTERS = [
  { id: 'all',      label: 'All' },
  { id: 'sms',      label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'voice',    label: 'Voice' },
  { id: 'demo',     label: 'Demo' },
  { id: 'notes',    label: 'Notes' },
];

// ── Time Helpers ──────────────────────────────────────────────────────────────
const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins > 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const absTime = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

// ── Contact Search ────────────────────────────────────────────────────────────
const ContactSearch = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiFetch(`/api/crm/contacts/search?q=${encodeURIComponent(query)}`);
        setResults(data.contacts || data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const pick = (c) => {
    onSelect(c);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative flex-1 max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search contact to view history…"
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 focus:border-[#843FEF] bg-white shadow-sm"
        />
        {loading && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {results.map((c) => (
            <button
              key={c.id || c._id}
              onClick={() => pick(c)}
              className="w-full text-left px-4 py-2.5 hover:bg-purple-50 flex justify-between items-center border-b border-gray-50 last:border-0"
            >
              <div>
                <span className="text-sm font-medium text-gray-800">{c.name || c.full_name}</span>
                <p className="text-xs text-gray-500">{c.organization || c.org}</p>
              </div>
              <span className="text-xs text-gray-400">{c.mobile || c.phone}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Add Note Modal ────────────────────────────────────────────────────────────
const AddNoteModal = ({ contactId, onClose, onSaved }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch('/api/crm/notes', {
        method: 'POST',
        body: JSON.stringify({ contact_id: contactId, note_text: text }),
      });
      onSaved();
    } catch {
      setError('Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-500" />
            Add Note
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {error && (
          <div className="mb-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        <textarea
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write your note here…"
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 resize-none"
          autoFocus
        />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !text.trim()}
            className="flex-1 py-2 bg-[#843FEF] text-white rounded-lg text-sm font-medium hover:bg-[#7230d4] disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Timeline Item ─────────────────────────────────────────────────────────────
const TimelineItem = ({ event, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cfg = EVENT_CONFIG[event.event_type] || {
    icon: Clock,
    color: 'bg-gray-100 text-gray-600',
    label: event.event_type || 'Event',
  };
  const Icon = cfg.icon;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this note?')) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/crm/notes/${event.id || event._id}`, { method: 'DELETE' });
      onDelete(event.id || event._id);
    } catch { /* silent */ }
    finally { setDeleting(false); }
  };

  const hasData = event.event_data && Object.keys(event.event_data).length > 0;
  const message = event.event_data?.message || event.event_data?.note_text || event.message;

  return (
    <div className="flex gap-4 group">
      {/* Left column: icon + line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${cfg.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 w-px bg-gray-200 mt-1 min-h-[20px]" />
      </div>

      {/* Right column: content */}
      <div className="flex-1 pb-5 min-w-0">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:border-[#843FEF]/20 transition-colors">
          <div
            className="flex items-start justify-between gap-3 px-4 py-3 cursor-pointer"
            onClick={() => hasData && setExpanded((e) => !e)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-800">{cfg.label}</span>
                {event.event_type === 'note_added' && (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="p-0.5 rounded text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete note"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {message && (
                <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{message}</p>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="text-right">
                <p
                  className="text-xs text-gray-400"
                  title={absTime(event.created_at || event.timestamp)}
                >
                  {relativeTime(event.created_at || event.timestamp)}
                </p>
                {hovered && (
                  <p className="text-xs text-gray-500 mt-0.5">{absTime(event.created_at || event.timestamp)}</p>
                )}
              </div>
              {hasData && (
                expanded
                  ? <ChevronUp className="w-4 h-4 text-gray-400" />
                  : <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </div>

          {/* Expanded event data */}
          {expanded && hasData && (
            <div className="px-4 pb-4 border-t border-gray-100">
              <div className="mt-3 bg-gray-50 rounded-lg p-3 overflow-x-auto">
                <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap break-all">
                  {JSON.stringify(event.event_data, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CRMCommunicationHistory = () => {
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [timeline, setTimelineData] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [error, setError] = useState('');

  const fetchTimeline = useCallback(async (contactId, filter) => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/api/crm/history/${contactId}?filter=${filter}`);
      setTimelineData(data.timeline || data || []);
    } catch {
      setError('Failed to load communication history.');
      setTimelineData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedContact) {
      fetchTimeline(selectedContact.id || selectedContact._id, activeFilter);
    }
  }, [selectedContact, activeFilter, fetchTimeline]);

  const filteredTimeline = timeline.filter((event) => {
    if (activeFilter === 'all') return true;
    const cfg = EVENT_CONFIG[event.event_type];
    return cfg?.filter === activeFilter;
  });

  const handleDeleteNote = (id) => {
    setTimelineData((prev) => prev.filter((e) => (e.id || e._id) !== id));
  };

  const handleSelectContact = (c) => {
    setSelectedContact(c);
    setActiveFilter('all');
    setTimelineData([]);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {showNoteModal && selectedContact && (
        <AddNoteModal
          contactId={selectedContact.id || selectedContact._id}
          onClose={() => setShowNoteModal(false)}
          onSaved={() => {
            setShowNoteModal(false);
            fetchTimeline(selectedContact.id || selectedContact._id, activeFilter);
          }}
        />
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-[#843FEF]" />
          Communication History
        </h1>
        <p className="text-sm text-gray-500 mt-1">Unified timeline of all contact interactions</p>
      </div>

      {/* Top Bar: Contact selector + Add Note */}
      <div className="flex items-center gap-3 mb-5">
        <ContactSearch onSelect={handleSelectContact} />
        {selectedContact && (
          <>
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-xl text-sm">
              <div className="w-7 h-7 rounded-full bg-[#843FEF] text-white flex items-center justify-center text-xs font-bold">
                {(selectedContact.name || selectedContact.full_name || 'U')[0].toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-sm leading-tight">{selectedContact.name || selectedContact.full_name}</p>
                <p className="text-xs text-gray-500">{selectedContact.organization || selectedContact.org}</p>
              </div>
              <button
                onClick={() => { setSelectedContact(null); setTimelineData([]); }}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setShowNoteModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#843FEF] text-white rounded-xl text-sm font-medium hover:bg-[#7230d4] shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Note
            </button>
          </>
        )}
      </div>

      {!selectedContact ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-gray-200 shadow-sm">
          <MessageCircle className="w-16 h-16 text-gray-200 mb-4" />
          <p className="text-gray-500 font-medium text-lg">Select a contact</p>
          <p className="text-sm text-gray-400 mt-1">Search for a contact above to view their communication history</p>
        </div>
      ) : (
        <div className="flex gap-5">
          {/* Filter Sidebar */}
          <div className="w-40 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2 sticky top-6">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 py-1 mb-1">Filter</p>
              {FILTERS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveFilter(id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeFilter === id
                      ? 'bg-[#843FEF] text-white'
                      : 'text-gray-600 hover:bg-purple-50 hover:text-[#843FEF]'
                  }`}
                >
                  {label}
                  {id !== 'all' && (
                    <span className={`ml-1 text-xs ${activeFilter === id ? 'text-purple-200' : 'text-gray-400'}`}>
                      ({timeline.filter((e) => {
                        const cfg = EVENT_CONFIG[e.event_type];
                        return cfg?.filter === id;
                      }).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="flex-1 min-w-0">
            {error && (
              <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-20 bg-white rounded-2xl border border-gray-200">
                <div className="text-center">
                  <RefreshCw className="w-7 h-7 text-[#843FEF] animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Loading history…</p>
                </div>
              </div>
            ) : filteredTimeline.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-200">
                <Clock className="w-12 h-12 text-gray-200 mb-3" />
                <p className="text-gray-500 font-medium">No events found</p>
                <p className="text-sm text-gray-400 mt-1">
                  {activeFilter === 'all' ? 'No communication history yet.' : `No ${activeFilter} events found.`}
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {/* Count badge */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {filteredTimeline.length} event{filteredTimeline.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                {filteredTimeline.map((event) => (
                  <TimelineItem
                    key={event.id || event._id || `${event.event_type}-${event.created_at}`}
                    event={event}
                    onDelete={handleDeleteNote}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMCommunicationHistory;
